// Preview controller (§7): owns the viewer canvas, its WebGL2 context and a Renderer.
//  · renders coalesce through requestAnimationFrame (never more than one pending render);
//  · half the preview resolution while a slider is dragged, full on release;
//  · 100% zoom renders a viewport-sized tile from the full-resolution source (true pixels);
//  · histogram from a ≤512 px readback, throttled to 10 per second;
//  · survives webglcontextlost / restored by rebuilding the renderer and re-uploading the source.
import { needsPasses, Renderer, PREVIEW_OUTPUT, type OutputOptions } from './renderer';
import {
  apply3,
  fullOutputSize,
  inv3,
  mul3,
  outputToSource,
  type GeometryInput,
  type Rect,
} from './geometry';
import { defaultSettings } from '../model/defaults';
import type { LocalMask, Settings } from '../model/types';
import type { DecodedImage } from '../io/decode/types';
import { computeHistogram, type Histogram } from './histogram';
import { grainCell } from './grain';
import { cssRgb } from '../app/theme';
type ObjectMatte = { id: string; key: string; width: number; height: number; data: Uint8Array };

export type ViewState = {
  settings: Settings;
  zoom: 'fit' | 1;
  pan: { x: number; y: number };
  before: boolean;
  split: boolean;
  splitPos: number;
  clipping: boolean;
  dragging: boolean;
  /** Crop tool active: show the whole rotated frame, not the crop. */
  showFrame: boolean;
  /** Red overlay of one mask (O) or of the brush being painted. */
  overlay?: { masks: LocalMask[]; index: number } | null;
  /** §11.12 view mode while the background is removed (1 checker … 5 matte). */
  bgView?: number;
};

export type Layout = {
  /** Image rect inside the viewport, CSS pixels. */
  rect: Rect;
  /** Output pixel size of what is drawn (device pixels, or the tile at 100%). */
  outW: number;
  outH: number;
  /** Geometry used for this view (frame mode overrides the crop). */
  geometry: GeometryInput;
  /** Size of the full-resolution output (crop) in pixels. */
  full: { width: number; height: number };
  zoom: 'fit' | 1;
  dpr: number;
};

const FIT_MARGIN = 16;

export class PreviewController {
  private gl: WebGL2RenderingContext;
  private renderer: Renderer;
  private img: DecodedImage | null = null;
  private state: ViewState | null = null;
  private size = { w: 1, h: 1, dpr: 1 };
  private raf = 0;
  private lost = false;
  private lastHist = 0;
  private histTimer = 0;
  layout: Layout | null = null;
  /** Milliseconds of the last render (CPU-side submit + finish when measuring). */
  lastFrameMs = 0;

  constructor(
    readonly canvas: HTMLCanvasElement,
    private onHistogram?: (h: Histogram) => void,
    private onLayout?: (l: Layout | null) => void,
  ) {
    const gl = canvas.getContext('webgl2', {
      premultipliedAlpha: false,
      alpha: false,
      antialias: false,
      preserveDrawingBuffer: true,
    });
    if (!gl) throw new Error('WebGL2 unavailable');
    this.gl = gl;
    // Draw in Display P3 so wide-gamut colour is not clipped on capable screens.
    if ('drawingBufferColorSpace' in gl)
      (gl as unknown as { drawingBufferColorSpace: string }).drawingBufferColorSpace = 'display-p3';
    this.renderer = new Renderer(gl);
    canvas.addEventListener('webglcontextlost', this.onLost);
    canvas.addEventListener('webglcontextrestored', this.onRestored);
  }

  private onLost = (e: Event) => {
    e.preventDefault();
    this.lost = true;
    cancelAnimationFrame(this.raf);
  };
  private onRestored = () => {
    this.lost = false;
    this.renderer = new Renderer(this.gl);
    if (this.img) this.renderer.setSource(this.img);
    // Everything else the viewer pushed in must come back too, or the cutout, LUT and grain silently
    // vanish from the view after a GPU reset (CODE_REVIEW S7).
    this.renderer.setMatte(this.last.matte, this.last.matteKey);
    this.renderer.setObjectMattes(this.last.objects);
    this.renderer.setLut(this.last.lut, this.last.lutId);
    if (this.last.seed !== null) this.renderer.setGrainSeed(this.last.seed);
    this.request();
  };
  private last: {
    matte: { width: number; height: number; data: Uint8Array } | null;
    matteKey: string;
    lut: import('../features/lut/cube').CubeLut | null;
    lutId: string;
    seed: number | null;
    objects: ObjectMatte[];
  } = { matte: null, matteKey: '', lut: null, lutId: '', seed: null, objects: [] };

  get engine() {
    return this.renderer;
  }

  /** The base background matte of the shown photo (null = none yet / not removed). */
  setMatte(m: { width: number; height: number; data: Uint8Array } | null, key = '') {
    this.last.matte = m;
    this.last.matteKey = key;
    this.renderer.setMatte(m, key);
    this.request();
  }

  /** M22: the object-pick mattes of the shown photo's object masks. */
  setObjectMattes(list: ObjectMatte[]) {
    this.last.objects = list;
    this.renderer.setObjectMattes(list);
    this.request();
  }

  /** The `.cube` LUT the shown settings point at (null = none / not loaded). */
  setLut(l: import('../features/lut/cube').CubeLut | null, id = '') {
    this.last.lut = l;
    this.last.lutId = id;
    this.renderer.setLut(l, id);
    this.request();
  }

  /** The shown photo's grain seed (§11.19). */
  setGrainSeed(seed: number) {
    this.last.seed = seed;
    this.renderer.setGrainSeed(seed);
  }

  setImage(img: DecodedImage | null) {
    this.img = img;
    if (img) {
      this.renderer.setSource(img);
      // setSource drops the mattes. The viewer pushes the shown photo's mattes as soon as the photo is chosen,
      // usually BEFORE its decode finishes (from memory), so they are put back here, or a cutout or an object
      // mask vanishes from the view after a photo switch or a Make bigger toggle while export still has it.
      this.renderer.setMatte(this.last.matte, this.last.matteKey);
      this.renderer.setObjectMattes(this.last.objects);
    } else this.renderer.disposeSource();
    this.request();
  }

  resize(cssW: number, cssH: number, dpr: number) {
    this.size = { w: Math.max(1, cssW), h: Math.max(1, cssH), dpr };
    this.canvas.width = Math.round(this.size.w * dpr);
    this.canvas.height = Math.round(this.size.h * dpr);
    this.request();
  }

  setState(s: ViewState) {
    this.state = s;
    this.request();
  }

  request() {
    if (this.raf || this.lost) return;
    this.raf = requestAnimationFrame(() => {
      this.raf = 0;
      this.renderNow();
    });
  }

  computeLayout(s: ViewState): Layout | null {
    const src = this.renderer.source;
    if (!src) return null;
    const g: GeometryInput = s.showFrame
      ? { ...s.settings.geometry, crop: { x: 0, y: 0, w: 1, h: 1 } }
      : s.settings.geometry;
    const full = fullOutputSize(g, src.width, src.height);
    const { w, h, dpr } = this.size;
    if (s.zoom === 1) {
      const outW = Math.min(full.width, Math.round(w * dpr));
      const outH = Math.min(full.height, Math.round(h * dpr));
      const rect = { x: (w - outW / dpr) / 2, y: (h - outH / dpr) / 2, w: outW / dpr, h: outH / dpr };
      return { rect, outW, outH, geometry: g, full, zoom: 1, dpr };
    }
    const k = Math.min((w - 2 * FIT_MARGIN) / full.width, (h - 2 * FIT_MARGIN) / full.height);
    const cw = Math.max(1, full.width * k),
      ch = Math.max(1, full.height * k);
    const rect = { x: (w - cw) / 2, y: (h - ch) / 2, w: cw, h: ch };
    let outW = Math.max(1, Math.round(cw * dpr));
    let outH = Math.max(1, Math.round(ch * dpr));
    if (s.dragging) {
      outW = Math.max(1, Math.round(outW / 2));
      outH = Math.max(1, Math.round(outH / 2));
    }
    return { rect, outW, outH, geometry: g, full, zoom: 'fit', dpr };
  }

  /** Clamps a 100%-view pan so the viewport stays over the image. */
  clampPan(pan: { x: number; y: number }, l: Layout): { x: number; y: number } {
    return {
      x: Math.min(Math.max(0, pan.x), Math.max(0, l.full.width - l.outW)),
      y: Math.min(Math.max(0, pan.y), Math.max(0, l.full.height - l.outH)),
    };
  }

  renderNow() {
    const s = this.state;
    const gl = this.gl;
    if (!s || this.lost) return;
    const t0 = performance.now();
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.canvas.width, this.canvas.height);
    // M18: the neutral photo surround (mid-gray in both themes, global.css --photo-surround).
    const [sr, sg, sb] = cssRgb('--photo-surround');
    gl.clearColor(sr, sg, sb, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    const layout = this.computeLayout(s);
    this.layout = layout;
    this.onLayout?.(layout);
    if (!layout || !this.img) return;
    const before = beforeSettings(s.settings);
    const bg = s.settings.background;
    const out: OutputOptions = {
      ...PREVIEW_OUTPUT,
      clipping: s.clipping,
      view: !s.before && bg.mode !== 'off' && this.renderer.hasMatte ? (s.bgView ?? 1) : 0,
    };
    const gr = s.settings.effects.grain;
    if (!s.before && gr.amount > 0) {
      // Grain in full-output coordinates so the preview matches the export (§11.19).
      const drawnW = Math.round(layout.rect.w * layout.dpr);
      const pan = layout.zoom === 1 ? this.clampPan(s.pan, layout) : { x: 0, y: 0 };
      const k = layout.zoom === 1 ? 1 : layout.full.width / Math.max(1, drawnW);
      out.grain = {
        amount: gr.amount / 100,
        cell: grainCell(gr.size, layout.full.width, layout.full.height),
        map: [k, k, pan.x, pan.y],
      };
    }
    if (!s.before && bg.mode !== 'off' && bg.fill.kind === 'color' && (s.bgView ?? 1) < 4) {
      out.flatten = true;
      out.fill = bg.fill.color; // encoded, as picked (the exporter uses it the same way)
    }
    const dprRect = {
      x: layout.rect.x * layout.dpr,
      y: layout.rect.y * layout.dpr,
      w: layout.rect.w * layout.dpr,
      h: layout.rect.h * layout.dpr,
    };
    const render = (st: Settings) => {
      if (layout.zoom === 1) {
        const pan = this.clampPan(s.pan, layout);
        return this.renderer.renderTile(
          { ...st, geometry: { ...st.geometry, ...layout.geometry } },
          layout.full,
          { x: pan.x, y: pan.y, w: layout.outW, h: layout.outH },
          false,
        );
      }
      return this.renderer.renderPreview(st, layout.outW, layout.outH, layout.geometry);
    };
    const shown = s.before ? before : s.settings;
    const splitX = dprRect.x + dprRect.w * s.splitPos;
    // Detail and skin need neighbourhood passes, so they cannot use the fused fit-view pass.
    if (layout.zoom === 1 || needsPasses(shown) || (s.split && !s.before && needsPasses(before))) {
      this.renderer.presentAt(render(shown), dprRect, this.canvas.height, out);
      if (s.split && !s.before)
        this.renderer.presentAt(render(before), dprRect, this.canvas.height, out, splitX);
    } else {
      // Fit view: one fused pass straight to the canvas (frame-time target, see colorShader.ts).
      this.renderer.drawPreview(shown, dprRect, this.canvas.height, out, layout.geometry);
      if (s.split && !s.before)
        this.renderer.drawPreview(before, dprRect, this.canvas.height, out, layout.geometry, splitX);
    }
    if (s.overlay && !s.before && s.overlay.masks[s.overlay.index])
      this.renderer.drawMaskOverlay(
        s.overlay.masks,
        s.overlay.index,
        dprRect,
        this.canvas.height,
        this.viewMatrix(layout),
      );
    this.lastFrameMs = performance.now() - t0;
    this.scheduleHistogram(shown, layout);
  }

  private scheduleHistogram(st: Settings, layout: Layout) {
    if (!this.onHistogram) return;
    const now = performance.now();
    const run = () => {
      this.lastHist = performance.now();
      // A separate small (<= 512 px) render of the same view: cheap and independent of the screen.
      const k = Math.min(1, 512 / Math.max(layout.full.width, layout.full.height));
      const w = Math.max(1, Math.round(layout.full.width * k));
      const h = Math.max(1, Math.round(layout.full.height * k));
      const t = this.renderer.renderPreview(st, w, h, layout.geometry);
      this.onHistogram?.(computeHistogram(this.renderer.readSmall(t, 512).data));
    };
    clearTimeout(this.histTimer);
    if (now - this.lastHist >= 100) run();
    else this.histTimer = window.setTimeout(() => this.request(), 100 - (now - this.lastHist));
  }

  /** Drawn-image pixel (device px inside the image rect) → source-normalized. */
  private viewMatrix(l: Layout) {
    const src = this.renderer.source!;
    if (l.zoom === 1) {
      const pan = this.state ? this.clampPan(this.state.pan, l) : { x: 0, y: 0 };
      const m = outputToSource(l.geometry, src.width, src.height, l.full.width, l.full.height);
      return mul3(m, [1, 0, pan.x, 0, 1, pan.y, 0, 0, 1]);
    }
    return outputToSource(
      l.geometry,
      src.width,
      src.height,
      Math.round(l.rect.w * l.dpr),
      Math.round(l.rect.h * l.dpr),
    );
  }

  /** Source-normalized point → viewport CSS point (overlay handles). */
  sourceToView(u: number, v: number): [number, number] | null {
    const l = this.layout;
    if (!l || !this.renderer.source) return null;
    const [x, y] = apply3(inv3(this.viewMatrix(l)), u, v);
    return [l.rect.x + x / l.dpr, l.rect.y + y / l.dpr];
  }

  /** Source size in pixels (for brush radii). */
  get sourceSize(): { width: number; height: number } | null {
    return this.renderer.source;
  }

  /**
   * Luminance sampler around source pixel (cx, cy) ± extent (heal source auto-pick): one readback of the
   * preview texture, then nearest-pixel lookups in source pixel coordinates.
   */
  lumaSampler(cx: number, cy: number, extent: number): (x: number, y: number) => number {
    const src = this.renderer.source;
    const blk =
      src &&
      this.renderer.readPreviewRect(
        (cx - extent) / src.width,
        (cy - extent) / src.height,
        (cx + extent) / src.width,
        (cy + extent) / src.height,
      );
    if (!src || !blk) return () => 0;
    return (x, y) => {
      const i = Math.min(blk.w - 1, Math.max(0, Math.floor(x * blk.sx - blk.x0)));
      const j = Math.min(blk.h - 1, Math.max(0, Math.floor(y * blk.sy - blk.y0)));
      const k = (j * blk.w + i) * 4;
      const a = blk.data[k + 3]! || 1;
      return (0.2289746 * blk.data[k]! + 0.6917385 * blk.data[k + 1]! + 0.0792869 * blk.data[k + 2]!) / a;
    };
  }

  /** Viewport CSS point → source-normalized point (or null outside the image). */
  viewToSource(px: number, py: number): [number, number] | null {
    const l = this.layout;
    const src = this.renderer.source;
    if (!l || !src) return null;
    const ox =
      ((px - l.rect.x) / l.rect.w) * (l.zoom === 1 ? l.outW : l.full.width) +
      (l.zoom === 1 ? (this.state ? this.clampPan(this.state.pan, l).x : 0) : 0);
    const oy =
      ((py - l.rect.y) / l.rect.h) * (l.zoom === 1 ? l.outH : l.full.height) +
      (l.zoom === 1 ? (this.state ? this.clampPan(this.state.pan, l).y : 0) : 0);
    const m = outputToSource(l.geometry, src.width, src.height, l.full.width, l.full.height);
    const [u, v] = apply3(m, ox, oy);
    return u >= 0 && v >= 0 && u <= 1 && v <= 1 ? [u, v] : null;
  }

  /** Eyedropper sample (§6.3): linear, after exposure, before WB, 5×5 average. */
  sampleForWb(px: number, py: number): [number, number, number] | null {
    const p = this.viewToSource(px, py);
    if (!p || !this.state) return null;
    const c = this.renderer.readPreviewBlock(p[0], p[1], 2);
    if (!c) return null;
    const gain = 2 ** this.state.settings.light.exposure;
    return [c[0] * gain, c[1] * gain, c[2] * gain];
  }

  dispose() {
    cancelAnimationFrame(this.raf);
    clearTimeout(this.histTimer);
    this.canvas.removeEventListener('webglcontextlost', this.onLost);
    this.canvas.removeEventListener('webglcontextrestored', this.onRestored);
    this.renderer.dispose();
  }
}

/** "Before" (§11.9) is the source with only geometry applied, so the crop does not jump. */
export function beforeSettings(s: Settings): Settings {
  const d = defaultSettings();
  d.geometry = s.geometry;
  return d;
}
